/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import {
  AppConfirmModal,
  type AppConfirmModalOption,
  type AppModalTone,
} from "@/components/ui/app-confirm-modal";
import { AppFeedbackModal } from "@/components/ui/app-feedback-modal";

type ModalRequest =
  | {
      type: "alert";
      title: string;
      description?: string;
      tone?: AppModalTone;
      confirmText?: string;
      resolve: () => void;
    }
  | {
      type: "confirm";
      title: string;
      description?: string;
      tone?: AppModalTone;
      confirmText?: string;
      cancelText?: string;
      resolve: (value: boolean) => void;
    }
  | {
      type: "prompt";
      title: string;
      description?: string;
      tone?: AppModalTone;
      label: string;
      placeholder?: string;
      required?: boolean;
      confirmText?: string;
      cancelText?: string;
      validate?: (value: string) => string | null;
      resolve: (value: string | null) => void;
    }
  | {
      type: "choose";
      title: string;
      description?: string;
      tone?: AppModalTone;
      options: AppConfirmModalOption[];
      resolve: (value: string | null) => void;
    };

export interface AppModalApi {
  alert: (options: Omit<Extract<ModalRequest, { type: "alert" }>, "type" | "resolve">) => Promise<void>;
  confirm: (
    options: Omit<Extract<ModalRequest, { type: "confirm" }>, "type" | "resolve">,
  ) => Promise<boolean>;
  prompt: (
    options: Omit<Extract<ModalRequest, { type: "prompt" }>, "type" | "resolve">,
  ) => Promise<string | null>;
  choose: (
    options: Omit<Extract<ModalRequest, { type: "choose" }>, "type" | "resolve">,
  ) => Promise<string | null>;
}

const AppModalContext = createContext<AppModalApi | null>(null);

export function AppModalProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ModalRequest | null>(null);
  const [promptValue, setPromptValue] = useState("");
  const [promptError, setPromptError] = useState<string | null>(null);
  const queueRef = useRef<ModalRequest[]>([]);

  const dequeueOrClear = useCallback(() => {
    const next = queueRef.current.shift() ?? null;
    setRequest(next);
    setPromptValue("");
    setPromptError(null);
  }, []);

  const enqueue = useCallback((nextRequest: ModalRequest) => {
    if (request) {
      queueRef.current.push(nextRequest);
      return;
    }
    setRequest(nextRequest);
  }, [request]);

  const api = useMemo<AppModalApi>(
    () => ({
      alert: (options) =>
        new Promise<void>((resolve) => {
          enqueue({ type: "alert", ...options, resolve });
        }),
      confirm: (options) =>
        new Promise<boolean>((resolve) => {
          enqueue({ type: "confirm", ...options, resolve });
        }),
      prompt: (options) =>
        new Promise<string | null>((resolve) => {
          enqueue({ type: "prompt", ...options, resolve });
        }),
      choose: (options) =>
        new Promise<string | null>((resolve) => {
          enqueue({ type: "choose", ...options, resolve });
        }),
    }),
    [enqueue],
  );

  return (
    <AppModalContext.Provider value={api}>
      {children}

      {request?.type === "alert" ? (
        <AppFeedbackModal
          open
          title={request.title}
          description={request.description}
          tone={request.tone}
          confirmText={request.confirmText}
          onClose={() => {
            dequeueOrClear();
            requestAnimationFrame(() => request.resolve());
          }}
        />
      ) : null}

      {request?.type === "confirm" ? (
        <AppConfirmModal
          open
          title={request.title}
          description={request.description}
          tone={request.tone}
          confirmText={request.confirmText}
          cancelText={request.cancelText}
          onCancel={() => {
            dequeueOrClear();
            requestAnimationFrame(() => request.resolve(false));
          }}
          onConfirm={() => {
            dequeueOrClear();
            requestAnimationFrame(() => request.resolve(true));
          }}
        />
      ) : null}

      {request?.type === "prompt" ? (
        <AppConfirmModal
          open
          title={request.title}
          description={request.description}
          tone={request.tone}
          confirmText={request.confirmText}
          cancelText={request.cancelText}
          promptLabel={request.label}
          promptPlaceholder={request.placeholder}
          promptValue={promptValue}
          promptError={promptError}
          onPromptValueChange={(value) => {
            setPromptValue(value);
            if (promptError) setPromptError(null);
          }}
          onCancel={() => {
            dequeueOrClear();
            requestAnimationFrame(() => request.resolve(null));
          }}
          onConfirm={() => {
            const value = promptValue.trim();
            if (request.required && !value) {
              setPromptError("Este campo es obligatorio.");
              return;
            }
            const customError = request.validate?.(value) ?? null;
            if (customError) {
              setPromptError(customError);
              return;
            }
            dequeueOrClear();
            requestAnimationFrame(() => request.resolve(value || null));
          }}
        />
      ) : null}

      {request?.type === "choose" ? (
        <AppConfirmModal
          open
          title={request.title}
          description={request.description}
          tone={request.tone}
          options={request.options}
          onCancel={() => {
            dequeueOrClear();
            requestAnimationFrame(() => request.resolve(null));
          }}
          onConfirm={() => {
            dequeueOrClear();
            requestAnimationFrame(() => request.resolve(null));
          }}
          onChoose={(id) => {
            dequeueOrClear();
            requestAnimationFrame(() => request.resolve(id));
          }}
        />
      ) : null}
    </AppModalContext.Provider>
  );
}

export function useAppModal(): AppModalApi {
  const context = useContext(AppModalContext);
  if (!context) {
    throw new Error("useAppModal must be used inside AppModalProvider");
  }
  return context;
}
